/** Shared hospital-portal display helpers (travel, applicants, positions). */

/** True when an applicant/order row is confirmed (legacy numeric + V2 string statuses). */
export function isConfirmedApplicantStatus(status: unknown): boolean {
  const raw = String(status ?? "").trim();
  if (!raw) return false;
  if (raw === "2") return true;
  const lower = raw.toLowerCase();
  return (
    lower === "confirmed" ||
    lower === "accepted" ||
    lower === "approved" ||
    lower === "filled"
  );
}

export function isConfirmedApplicant(applicant: {
  status?: unknown;
  userId?: string;
}): boolean {
  return isConfirmedApplicantStatus(applicant.status);
}

export function getConfirmedApplicantUserIds(
  applicants: Array<{ status?: unknown; userId?: string }>,
): Set<string> {
  const ids = new Set<string>();
  for (const a of applicants) {
    if (isConfirmedApplicant(a) && a.userId) {
      ids.add(String(a.userId));
    }
  }
  return ids;
}

export function partitionApplicants<T extends { status?: unknown; userId?: string }>(
  applicants: T[],
  extraConfirmedUserIds?: Iterable<string>,
): { confirmed: T[]; pending: T[] } {
  const confirmedIds = getConfirmedApplicantUserIds(applicants);
  if (extraConfirmedUserIds) {
    Array.from(extraConfirmedUserIds).forEach((id) => {
      if (id) confirmedIds.add(String(id));
    });
  }
  const confirmed: T[] = [];
  const pending: T[] = [];
  for (const a of applicants) {
    if (isConfirmedApplicant(a) || (a.userId && confirmedIds.has(String(a.userId)))) {
      confirmed.push(a);
    } else {
      pending.push(a);
    }
  }
  return { confirmed, pending };
}

function coalesceTravelText(...values: unknown[]): string {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
}

function coalesceTravelRate(...values: unknown[]): number | string | null | undefined {
  for (const value of values) {
    if (value == null || value === "") continue;
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) {
      return typeof value === "number" || typeof value === "string" ? value : n;
    }
  }
  return null;
}

const GENERIC_TRAVEL_LABEL = /^(travel\s*included|not\s*included)$/i;

export function resolveTravelIncludedFlag(input: {
  travel?: unknown;
  travelFlag?: unknown;
  fixedRate?: unknown;
  variableRate?: unknown;
  otherRate?: unknown;
}): boolean {
  if (
    input.travelFlag === 1 ||
    input.travelFlag === true ||
    String(input.travelFlag) === "1"
  ) {
    return true;
  }
  const toRate = (value: unknown): number => {
    const n = Number(String(value ?? "").replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };
  const fixed = toRate(input.fixedRate);
  const variable = toRate(input.variableRate);
  if (fixed > 0 || variable > 0) return true;
  return Boolean(
    coalesceTravelText(input.otherRate, input.travel),
  );
}

export function resolveTravelDisplayText(input: {
  travel?: string | null;
  travelFlag?: number | boolean | null;
  fixedRate?: number | string | null;
  variableRate?: number | string | null;
  fightsIncludedFlag?: number | boolean | null;
  otherRate?: string | null;
}): string {
  const otherRate = coalesceTravelText(input.otherRate);
  const travelText = coalesceTravelText(input.travel);
  const fixedRate = coalesceTravelRate(input.fixedRate);
  const variableRate = coalesceTravelRate(input.variableRate);

  const included = resolveTravelIncludedFlag({
    travel: travelText,
    travelFlag: input.travelFlag,
    fixedRate,
    variableRate,
    otherRate,
  });
  if (!included) {
    return "Not included";
  }

  if (travelText && !GENERIC_TRAVEL_LABEL.test(travelText)) {
    return travelText;
  }

  return formatTravelDisplay({
    travelFlag: 1,
    fixedRate,
    variableRate,
    fightsIncludedFlag: input.fightsIncludedFlag,
    otherRate: otherRate || travelText,
  });
}

export function formatTravelDisplay(input: {
  travelFlag?: number | boolean | null;
  fixedRate?: number | string | null;
  variableRate?: number | string | null;
  fightsIncludedFlag?: number | boolean | null;
  otherRate?: string | null;
}): string {
  const travelFlag =
    input.travelFlag === 1 ||
    input.travelFlag === true ||
    String(input.travelFlag) === "1";

  if (!travelFlag) {
    return "Not included";
  }

  const otherRateText = String(input.otherRate ?? "").trim();
  const fixedRateNum = Number(input.fixedRate ?? 0);
  const variableRateNum = Number(input.variableRate ?? 0);

  if (otherRateText && isZeroTravelRateText(otherRateText)) {
    return "Travel included";
  }

  if (otherRateText && fixedRateNum <= 0 && variableRateNum <= 0) {
    return otherRateText;
  }

  if (fixedRateNum > 0) {
    return `$${formatMoney(fixedRateNum)} total`;
  }

  if (variableRateNum > 0) {
    const perKm = formatMoney(variableRateNum);
    if (isZeroTravelRateText(`$${perKm}/km`)) {
      return "Travel included";
    }
    const flights =
      input.fightsIncludedFlag === 1 ||
      input.fightsIncludedFlag === true ||
      String(input.fightsIncludedFlag) === "1";
    return `$${perKm}/km${flights ? ", flights included" : ""}`;
  }

  if (otherRateText) {
    return isZeroTravelRateText(otherRateText) ? "Travel included" : otherRateText;
  }

  return "Travel included";
}

function isZeroTravelRateText(value: string): boolean {
  const normalized = value.trim().toLowerCase().replace(/\s+/g, "");
  if (!normalized) return true;
  if (
    normalized === "$0" ||
    normalized === "$0.00" ||
    normalized === "$0/km" ||
    normalized === "$0.0/km" ||
    normalized === "$0.00/km" ||
    normalized === "0/km" ||
    normalized === "0.0/km" ||
    normalized === "0.00/km" ||
    normalized === "included" ||
    normalized === "travelincluded"
  ) {
    return true;
  }
  // Only treat explicit zero per-km rates as empty — not "0.80/km" or "0/km and flights".
  return /^\$?0(\.0+)?\/km(?:[^0-9.]|$)/.test(normalized);
}

function formatMoney(value: number): string {
  const w = (window as any)?.utils?.formatMoneyActual?.(value, 0);
  if (w != null && String(w).length > 0) {
    return String(w).replace(/^\$/, "");
  }
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

export type ApplicantRelationshipKind =
  | "INTERNAL"
  | "PRE_CREDENTIAL_PRIORITY"
  | "PRE_CREDENTIAL"
  | "FAVORITE"
  | "WORKED_BEFORE"
  | "NEW";

export type ApplicantRelationshipFlags = {
  onStaff?: boolean;
  preCredentialed?: boolean;
  preCredentialMatch?: boolean;
  isFavourite?: boolean;
  isFavorited?: boolean;
  isFavoriteDoctor?: boolean;
  hasWorkedBefore?: boolean;
  hasWorkedAtHospital?: boolean;
  workedBefore?: boolean;
};

export type ApplicantRelationship = {
  kind: ApplicantRelationshipKind;
  label: string;
  rank: number;
  hideMatchScore: boolean;
};

const RELATIONSHIP_META: Record<
  ApplicantRelationshipKind,
  { label: string; rank: number }
> = {
  INTERNAL: { label: "Internal", rank: 5 },
  PRE_CREDENTIAL_PRIORITY: { label: "Pre-cred · Priority", rank: 4 },
  PRE_CREDENTIAL: { label: "Pre-credentialed", rank: 3 },
  FAVORITE: { label: "Favorite", rank: 2 },
  WORKED_BEFORE: { label: "Worked before", rank: 1 },
  NEW: { label: "New applicant", rank: 0 },
};

export function resolveApplicantRelationship(
  applicant: ApplicantRelationshipFlags,
): ApplicantRelationship {
  const onStaff = Boolean(applicant.onStaff);
  const preCredentialed = !onStaff && Boolean(applicant.preCredentialed);
  const preCredentialMatch =
    preCredentialed && Boolean(applicant.preCredentialMatch);
  const favorite = Boolean(
    applicant.isFavourite ||
      applicant.isFavorited ||
      applicant.isFavoriteDoctor,
  );
  const workedBefore = Boolean(
    applicant.hasWorkedBefore ||
      applicant.hasWorkedAtHospital ||
      applicant.workedBefore,
  );

  let kind: ApplicantRelationshipKind = "NEW";
  if (onStaff) kind = "INTERNAL";
  else if (preCredentialMatch) kind = "PRE_CREDENTIAL_PRIORITY";
  else if (preCredentialed) kind = "PRE_CREDENTIAL";
  else if (favorite) kind = "FAVORITE";
  else if (workedBefore) kind = "WORKED_BEFORE";

  const meta = RELATIONSHIP_META[kind];
  return {
    kind,
    label: meta.label,
    rank: meta.rank,
    hideMatchScore: kind === "INTERNAL" || preCredentialed,
  };
}

export function applicantRelationshipRank(
  applicant: ApplicantRelationshipFlags,
): number {
  return resolveApplicantRelationship(applicant).rank;
}

export function preCredentialApplicantRank(applicant: {
  onStaff?: boolean;
  preCredentialed?: boolean;
  preCredentialMatch?: boolean;
  isFavourite?: boolean;
  isFavorited?: boolean;
  hasWorkedBefore?: boolean;
  hasWorkedAtHospital?: boolean;
  workedBefore?: boolean;
}): number {
  return applicantRelationshipRank(applicant);
}

export function sortApplicantsByPreCredential<
  T extends ApplicantRelationshipFlags,
>(applicants: T[]): T[] {
  return [...applicants].sort(
    (a, b) => applicantRelationshipRank(b) - applicantRelationshipRank(a),
  );
}

/** Specialty points are 40. Experience-only leftovers must not look like a match. */
export const CLINICAL_MATCH_MIN_SCORE = 40;

export function shouldShowMatchScore(applicant: {
  onStaff?: boolean;
  preCredentialed?: boolean;
  matchScore?: number | null;
}): boolean {
  if (applicant.onStaff || applicant.preCredentialed) return false;
  return (applicant.matchScore ?? 0) >= CLINICAL_MATCH_MIN_SCORE;
}

export function resolveMatchScore(applicant: {
  matchScore?: number | null;
  score?: number | null;
}): number {
  if (typeof applicant.matchScore === "number" && !Number.isNaN(applicant.matchScore)) {
    return applicant.matchScore;
  }
  return 0;
}

export function formatPositionsLabel(
  confirmed?: number | null,
  required?: number | null,
): string | null {
  const req = typeof required === "number" && required > 0 ? required : 0;
  const conf = typeof confirmed === "number" ? confirmed : 0;
  if (req <= 1) return null;
  return `${conf}/${req} filled`;
}

export function isMultiPositionPartial(
  confirmed?: number | null,
  required?: number | null,
): boolean {
  const req = typeof required === "number" && required > 0 ? required : 1;
  const conf = typeof confirmed === "number" ? confirmed : 0;
  return req > 1 && conf < req;
}

export function preCredentialBadgeLabel(applicant: {
  onStaff?: boolean;
  preCredentialed?: boolean;
  preCredentialMatch?: boolean;
}): string | null {
  const relationship = resolveApplicantRelationship(applicant);
  if (
    relationship.kind === "INTERNAL" ||
    relationship.kind === "PRE_CREDENTIAL_PRIORITY" ||
    relationship.kind === "PRE_CREDENTIAL"
  ) {
    return relationship.label;
  }
  return null;
}

export function relationshipBadgeLabel(
  applicant: ApplicantRelationshipFlags,
): string | null {
  const relationship = resolveApplicantRelationship(applicant);
  if (relationship.kind === "NEW") return null;
  return relationship.label;
}

export function applicantRelationshipCardClass(
  kind: ApplicantRelationshipKind,
  isAccepted?: boolean,
): string {
  if (isAccepted) {
    return "bg-gradient-to-br from-emerald-50 to-white border-emerald-300 hover:border-emerald-400";
  }
  switch (kind) {
    case "INTERNAL":
      return "bg-teal-50/70 border-teal-500 shadow-sm shadow-teal-100 hover:border-teal-600";
    case "PRE_CREDENTIAL_PRIORITY":
      return "bg-violet-50/70 border-violet-500 shadow-sm shadow-violet-100 hover:border-violet-600";
    case "PRE_CREDENTIAL":
      return "bg-violet-50/40 border-violet-300 hover:border-violet-400";
    case "FAVORITE":
      return "bg-white border-pink-300 hover:border-pink-400";
    default:
      return "bg-white border-[#e6e9f0] hover:border-[#d8e0ff]";
  }
}

export function applicantRelationshipBadgeClass(
  kind: ApplicantRelationshipKind,
): string {
  switch (kind) {
    case "INTERNAL":
      return "text-white bg-teal-600 border border-teal-700";
    case "PRE_CREDENTIAL_PRIORITY":
      return "text-white bg-violet-600 border border-violet-700";
    case "PRE_CREDENTIAL":
      return "text-violet-800 bg-violet-50 border border-violet-200";
    case "FAVORITE":
      return "text-pink-700 bg-pink-50 border border-pink-200";
    case "WORKED_BEFORE":
      return "text-green-700 bg-green-50 border border-green-200";
    default:
      return "text-gray-600 bg-gray-50 border border-gray-200";
  }
}
